import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import type { TypographyProps } from '@mui/material';
import TypographyMaterial from '@mui/material/Typography';
import { useTheme } from '../../providers/StyleProvider';
const useColor = t0 => {
  const {
    muted
  } = t0;
  const theme = useTheme();
  const color = muted ? theme.text.status.neutral : theme.text.default;
  return color;
};
const convertToCaps = (text: string | number) => {
  if (typeof text === 'string') {
    return text.toUpperCase();
  }
  return text;
};
type CommonTypographyProps = {
  text: string | number;
  muted?: boolean;
  caps?: boolean;
} & TypographyProps;
const SubtitleBold: React.FC<CommonTypographyProps> = t0 => {
  const $ = _c(16);
  let caps;
  let muted;
  let props;
  let text;
  if ($[0] !== t0) {
    ({
      text,
      muted,
      caps,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = caps;
    $[2] = muted;
    $[3] = props;
    $[4] = text;
  } else {
    caps = $[1];
    muted = $[2];
    props = $[3];
    text = $[4];
  }
  let t1;
  if ($[5] !== muted) {
    t1 = {
      muted
    };
    $[5] = muted;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const color = useColor(t1);
  let t2;
  if ($[7] !== color) {
    t2 = {
      fontWeight: "600",
      color
    };
    $[7] = color;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== caps || $[10] !== text) {
    t3 = caps ? convertToCaps(text) : text;
    $[9] = caps;
    $[10] = text;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== props || $[13] !== t2 || $[14] !== t3) {
    t4 = <TypographyMaterial variant="subtitle1" sx={t2} {...props}>{t3}</TypographyMaterial>;
    $[12] = props;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
const Subtitle: React.FC<CommonTypographyProps> = t0 => {
  const $ = _c(16);
  let caps;
  let muted;
  let props;
  let text;
  if ($[0] !== t0) {
    ({
      text,
      muted,
      caps,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = caps;
    $[2] = muted;
    $[3] = props;
    $[4] = text;
  } else {
    caps = $[1];
    muted = $[2];
    props = $[3];
    text = $[4];
  }
  let t1;
  if ($[5] !== muted) {
    t1 = {
      muted
    };
    $[5] = muted;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const color = useColor(t1);
  let t2;
  if ($[7] !== color) {
    t2 = {
      fontWeight: "500",
      color
    };
    $[7] = color;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== caps || $[10] !== text) {
    t3 = caps ? convertToCaps(text) : text;
    $[9] = caps;
    $[10] = text;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== props || $[13] !== t2 || $[14] !== t3) {
    t4 = <TypographyMaterial variant="subtitle1" sx={t2} {...props}>{t3}</TypographyMaterial>;
    $[12] = props;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
const Text: React.FC<CommonTypographyProps> = t0 => {
  const $ = _c(16);
  let caps;
  let muted;
  let props;
  let text;
  if ($[0] !== t0) {
    ({
      text,
      muted,
      caps,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = caps;
    $[2] = muted;
    $[3] = props;
    $[4] = text;
  } else {
    caps = $[1];
    muted = $[2];
    props = $[3];
    text = $[4];
  }
  let t1;
  if ($[5] !== muted) {
    t1 = {
      muted
    };
    $[5] = muted;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const color = useColor(t1);
  let t2;
  if ($[7] !== color) {
    t2 = {
      fontWeight: "none",
      color
    };
    $[7] = color;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== caps || $[10] !== text) {
    t3 = caps ? convertToCaps(text) : text;
    $[9] = caps;
    $[10] = text;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== props || $[13] !== t2 || $[14] !== t3) {
    t4 = <TypographyMaterial variant="inherit" sx={t2} {...props}>{t3}</TypographyMaterial>;
    $[12] = props;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
const Small: React.FC<CommonTypographyProps> = t0 => {
  const $ = _c(16);
  let caps;
  let muted;
  let props;
  let text;
  if ($[0] !== t0) {
    ({
      text,
      muted,
      caps,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = caps;
    $[2] = muted;
    $[3] = props;
    $[4] = text;
  } else {
    caps = $[1];
    muted = $[2];
    props = $[3];
    text = $[4];
  }
  let t1;
  if ($[5] !== muted) {
    t1 = {
      muted
    };
    $[5] = muted;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const color = useColor(t1);
  let t2;
  if ($[7] !== color) {
    t2 = {
      fontWeight: "none",
      color
    };
    $[7] = color;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== caps || $[10] !== text) {
    t3 = caps ? convertToCaps(text) : text;
    $[9] = caps;
    $[10] = text;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== props || $[13] !== t2 || $[14] !== t3) {
    t4 = <TypographyMaterial variant="subtitle2" sx={t2} {...props}>{t3}</TypographyMaterial>;
    $[12] = props;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
export const General = {
  Subtitle,
  SubtitleBold,
  Small,
  Text
};